import { useCallback, useEffect, useMemo, useState } from "react";
import { produce } from "immer";

import { getText } from "shared/lib";
import {
  newDiggingTreeDescriptor,
  TreeModuleEventTypes,
  type NewDiggingTreeDescriptorExtraOptions,
  type TreeDescriptor,
} from "./lib";

import { TreeList, type TreeListProps } from "./treeList";
import { DiggingTreeDialog } from "./DiggingTreeDialog";

export interface DiggingTreeListProps {
  isOtherTreeActivity?: boolean;
  newTreeOptions?: NewDiggingTreeDescriptorExtraOptions;
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onTreeDeleted?: (treeDescriptor: TreeDescriptor) => void;
  subTitle?: TreeListProps["subTitle"];
  title: TreeListProps["title"];
  treeDescriptors: Array<TreeDescriptor>;
  treesHaveBeenDeleted: boolean;
}

export function DiggingTreeList({
  isOtherTreeActivity,
  newTreeOptions,
  onChangeTreeDescriptors,
  onTreeDeleted,
  subTitle,
  title,
  treeDescriptors,
  treesHaveBeenDeleted,
}: DiggingTreeListProps) {
  const [treeDialogOpen, setTreeDialogOpen] = useState(false);

  const [selectedTreeDescriptorId, setSelectedTreeDescriptorId] = useState<
    string | undefined
  >();

  const selectedTreeDescriptor = useMemo(() => {
    return treeDescriptors.find((td) => td.treeId === selectedTreeDescriptorId);
  }, [selectedTreeDescriptorId, treeDescriptors]);

  const handleClickAdd = useCallback(() => {
    const lastTreeDescriptor = treeDescriptors.at(-1);
    let count = 0;
    if (lastTreeDescriptor) {
      count = parseInt(lastTreeDescriptor.title.split(" ").at(-1) || "0");
    }
    const newTreeDescriptor = newDiggingTreeDescriptor(
      {
        title: `${count + 1}`,
        getText,
      },
      newTreeOptions,
    );
    const newTreeDescriptors = treeDescriptors.concat(newTreeDescriptor);
    onChangeTreeDescriptors?.(newTreeDescriptors);
    setSelectedTreeDescriptorId(newTreeDescriptor.treeId);
    setTreeDialogOpen(true);
  }, [treeDescriptors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickDelete = (treeDescriptor: TreeDescriptor) => {
    onChangeTreeDescriptors?.(
      treeDescriptors.filter((td) => td.treeId !== treeDescriptor?.treeId),
    );
    onTreeDeleted?.(treeDescriptor);
  };

  const handleClickDuplicate = (treeDescriptor: TreeDescriptor) => {
    if (!treeDescriptor) return;

    const lastTreeDescriptor = treeDescriptors.at(-1);
    let count = 0;
    if (lastTreeDescriptor) {
      count = parseInt(lastTreeDescriptor.title.split(" ").at(-1) || "0");
    }

    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        draft.push({
          ...treeDescriptor,
          title: `${count + 1}`,
          treeId: crypto.randomUUID(),
        });
      }),
    );
  };

  useEffect(() => {
    document.addEventListener(
      TreeModuleEventTypes.AddDiggingTree,
      handleClickAdd,
    );

    return () => {
      document.removeEventListener(
        TreeModuleEventTypes.AddDiggingTree,
        handleClickAdd,
      );
    };
  }, [handleClickAdd]);

  return (
    <>
      <TreeList
        emptyText={getText(
          "You aren't proposing works within 15 metres of any trees.",
        )}
        isOtherTreeActivity={isOtherTreeActivity}
        onClick={(td) => {
          setSelectedTreeDescriptorId(td.treeId);
          setTreeDialogOpen(true);
        }}
        onClickAddTree={handleClickAdd}
        onClickDelete={handleClickDelete}
        onClickDuplicate={handleClickDuplicate}
        subTitle={subTitle}
        title={title}
        treeDescriptors={treeDescriptors}
        treesHaveBeenDeleted={treesHaveBeenDeleted}
      />

      <DiggingTreeDialog
        open={treeDialogOpen && !!selectedTreeDescriptor}
        onClickDelete={handleClickDelete}
        onClickNext={(td) => setSelectedTreeDescriptorId(td.treeId)}
        onClickPrev={(td) => setSelectedTreeDescriptorId(td.treeId)}
        onClickSubmit={() => setTreeDialogOpen(false)}
        onChangeTreeDescriptors={onChangeTreeDescriptors}
        onOpenChange={(open) => !open && setTreeDialogOpen(false)}
        treeDescriptor={selectedTreeDescriptor}
        treeDescriptors={treeDescriptors}
      />
    </>
  );
}
